/* eslint-disable max-lines */

// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import cstmTable from '../../../share/components/vue/PaginationTable';
import cstmPagination from '../../../share/components/vue/Pagination';
import loading from '../../../share/components/vue/ProcessingIcon';
import cstmCard from "../../../share/components/vue/CustomCard";
import svgButton from '../../../share/components/vue/SVGButton';
import util from "../../../../../manage/src/share/util/utils";
import config from '../../../share/config';
import filter from '../../../share/util/filter';
import alert from '../../../share/alert';
import lngSwitch from '../../../share/util/languageSwitching';
import api from '../../../common/api';
import handler from '../../../share/util/apiResultValidator';
const testPfcList = require('../../../data/testMode/pfcFileList.json');

const VUE_APP_FILE_PAGE_DISPLAY_COUNT = 5;
const REPRO_TARGETS = {
  normal: { apiType: 'normal', displayMode: 'campaign' },
  merchant: { apiType: 'merchantability', displayMode: 'update' },
  restore: { apiType: 'restore', displayMode: 'restore' }
};

export default {
  name: "systemFileTable",
  data() {
    return {
      processing: false,
      allFileDataList: [], // APIからのレスポンスデータ
      fileDataListByLang: [], // 選択言語のみ抽出したデータ
      targetUnitDetails: [], // 表示用データ
      systemTableColumns: [],
      // 検索関連
      searchServiceNews: '',
      searchReleaseDateRange: {
        startDate: null,
        endDate: null
      },
      // ページネーション関連
      pageSize: Number(VUE_APP_FILE_PAGE_DISPLAY_COUNT),
      currentPage: 0,
      totalItemLength: 0,
      // 表示言語切り替え対応
      translations: [],
      selectedLanguage: '',
      translationLangs: [],
      // リプロありなし判定
      isReprogrammable: false,
    };
  },
  watch: {
    // ユニット情報を監視 (主にツリービューからの遷移用)
    async unitInfo(newData, oldData) {
      if (newData && oldData && newData.unitId === oldData.unitId) return;
      await this.initData();
      if (newData && oldData) {
        if (newData.paneConfig.isExecutable) {
          this.showMultipleFileAlert();
        }
      }
    },
    // 言語情報の変更を監視 
    displayLang() {
      this.createListBySelectedLang();
      this.updateTranslationList();
    },
  },
  components: {
    'cstm-table': cstmTable,
    'cstm-pagination': cstmPagination,
    'processing-view': loading,
    'cstm-card': cstmCard,
    'svg-button': svgButton,
  },
  computed: {
    ...mapState({
      vehicleInfo: state => state.vehicle.vehicleInfo,
      unitInfo: state => state.targetUnit.unitInfo,
      paneConfig(state) {
        const ret = state.targetUnit.unitInfo ? state.targetUnit.unitInfo.paneConfig : {};
        return ret;
      },
      isTestMode: state => state.netAppConnector.isTestMode,
      displayMode: state => state.cache.showPublished, // true: 公開, false: 非公開
      displayReproTypeMode: state => state.cache.showDisplayVehicle,
      displayLang: state => state.systems.language,
      masterTranslations: state => state.fileResources.translations,
      cachedTranslationLang: state => state.userProperty.userCache[config.USER_CACHE_REPRO_SYSTEM_TRANSLATION],
      pairingAppId: state => state.netAppConnector.pairingAppId,
      merchandiseInfo: state => state.cache.selectMerchandiseInfo,
      cachedSystemTable: state => state.cache.systemTable,
    }),
    getDaysOnLocale: function () {
      return util.getDaysOnLocale();
    },
  },
  methods: {
    ...mapActions('userProperty', ['setUserCache']),
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('cache', ['cacheSelectTestModeFileData', 'cacheSelectMerchandiseInfo']),

    /**
     * File画面でどのリプロ対象APIを呼び出すかを決定する
     * RCRなど機能向上と復元の両方のリプロ有りとなる場合は両方の対象を返す
     * @returns {Array<{apiType: string, displayMode: string}>} APIに指定するリプロ種別とFile選択時に設定する表示モードの一覧
     */
    getReproTargets() {
      if (this.displayReproTypeMode === 'campaign' || this.displayReproTypeMode === 'normal') {
        return [REPRO_TARGETS.normal];
      }

      // VuexにキャッシュされたSystem一覧から現在選択中のSystemを探し、見つからなかった場合に表示モードから取得対象を補完する
      const table = this.displayMode ? this.cachedSystemTable?.published : this.cachedSystemTable?.unpublished;
      const system = table?.find(item => Number(item.id) === Number(this.unitInfo.unitId));
      if (!system) {
        if (this.displayReproTypeMode === 'updateChange') {
          return [REPRO_TARGETS.merchant, REPRO_TARGETS.restore];
        }
        return this.displayReproTypeMode === 'update' ? [REPRO_TARGETS.merchant] : [REPRO_TARGETS.restore];
      }

      // リプロ対象となるリプロ種別を判定する
      const executable = this.vehicleInfo?.vehicle?.is_su_flow ? system.showDetail : system.reproExecutable;
      const targets = [];
      if (executable?.merchant) targets.push(REPRO_TARGETS.merchant);
      if (executable?.restore) targets.push(REPRO_TARGETS.restore);
      return targets;
    },

    // システムテーブル画面に戻る
    back: function () {
      this.$emit('callSelectReproEcu', null);
      // Vehicle 画面のヘッダタブを表示
      if (this.vehicleInfo.vehicle.is_su_flow) {
        this.$router.push({ name: 'suSystemTable', params: { previousDisplayName: 'systemFileTable' } });
      } else {
        this.$router.push({ name: 'systemTable', params: { previousDisplayName: 'systemFileTable' } });
      }
    },
    // Checks for repro is possible or not
    isReproPossible() {
      if (this.unitInfo && this.allFileDataList) {
        if (this.unitInfo.paneConfig.isExecutable) {
          return this.unitInfo.unitId && this.allFileDataList.length;
        }
      }
      return false;
    },

    // データの格納
    async updateUnitInfo() {
      // 言語切り替え
      const executeUpdate = (pfcList) => {
        pfcList.sort((a, b) => {
          if (a.released_time < b.released_time) return 1;
          if (a.released_time > b.released_time) return -1;
          return 0;
        });
        this.allFileDataList.splice(0);
        this.allFileDataList = JSON.parse(JSON.stringify(pfcList));
        // 選択言語設定
        if (this.allFileDataList.length > 0) {
          this.createListBySelectedLang();
          this.updateTranslationList();
        }
      };
      if (!this.unitInfo) return;
      // リプロありなしを判定
      this.isReprogrammable = this.unitInfo.paneConfig.isExecutable;
      // File画面用のリプロ対象を取得
      const reproTargets = this.getReproTargets();
      if (this.isTestMode) {
        const targetFileId = testPfcList.pfcFileIdList.find((e) => parseInt(e.id) === this.unitInfo.unitId)?.pfc_file_id;
        if (targetFileId !== undefined && this.isReprogrammable) {
          const targetDataByReproType = Array.from(testPfcList.data.pfcs).map(file => ({
            ...file,
            pfc_id: targetFileId,
            reproTypeMode: reproTargets[0]?.displayMode || this.displayReproTypeMode
          }));
          executeUpdate(targetDataByReproType);
        }
      } else {
        if (this.isReprogrammable) {
          const endpoint = '/netApps/' + this.pairingAppId + '/' + this.unitInfo.unitId + '/reproTargets';
          this.processing = true;
          // RCRなど複数リプロ種別が存在する場合は、すべてのリプロ種別の情報を取得する
          const responses = await Promise.all(reproTargets.map(async target => ({
            target,
            response: await api.getParamCall(config.REPRO, endpoint, {
              repro_type: target.apiType,
              file_status: this.displayMode ? 'published' : 'unpublished',
              merchandise_code: this.merchandiseInfo
            })
          })));
          this.processing = false;

          const pfcList = [];
          for (const result of responses) {
            let isValid = false;
            handler.validate(
              handler.validateTypes.all,
              result.response, this, null,
              () => {
                isValid = true;
                result.response.data.pfcs.forEach(file => pfcList.push({
                  ...file,
                  reproTypeMode: result.target.displayMode
                }));
              },
              (errorResult) => {
                // ネットワークエラー・セッションエラーは警告を表示
                if (errorResult.causeType === handler.causeTypes.networkError ||
                  errorResult.causeType === handler.causeTypes.invalidSessionToken) {
                  alert.showWarning(this, null, errorResult.message, () => {
                    if (errorResult.behaviorType === handler.behaviorTypes.logout) {
                      this.logout(this);
                    }
                    else if (errorResult.behaviorType === handler.behaviorTypes.resetSession) {
                      this.resetSession(this);
                    }
                  });
                }
                else if (errorResult.causeType === handler.causeTypes.clientConnectorNotAliveError) {
                  // CC が起動していないなら強制サインアウト
                  alert.showError(this, null, errorResult.message,
                    () => { this.logout(this); }
                  );
                }
                else {
                  // その他のエラー
                  alert.showError(this, null, errorResult.message, () => {
                    if (errorResult.behaviorType === handler.behaviorTypes.logout) {
                      this.logout(this);
                    }
                    else if (errorResult.behaviorType === handler.behaviorTypes.resetSession) {
                      this.resetSession(this);
                    }
                  });
                }
              }, null, false);
            if (!isValid) return;
          }
          executeUpdate(pfcList);
        } else {
          this.processing = false;
        }
      }
    },

    clickRow(data) {
      // テストモードの時は選択された情報をキャッシュ
      if (this.isTestMode) {
        this.cacheSelectTestModeFileData(data);
      }
      this.$emit('selectReproSystem', {
        pfc_file_id: data.pfc_file_id,
        reproTypeMode: data.reproTypeMode
      });
    },

    // 検索関連
    // 検索の実行
    executeFilter: function (initPag = true) {
      if (initPag === true) {
        this.initPagination();
      }

      // Convert temporary registration date and published date ranges
      const convertDateRange = function (dateRange) {
        const convertedDate = { startUnix: null, endUnix: null };
        // 開始日・終了日の両方が設定されていなければ、正常な日付入力とみなさない
        if (dateRange.startDate && dateRange.endDate) {
          // 検索の開始時間を設定(時刻情報は必要ないので削除する)
          dateRange.startDate.setHours(0);
          dateRange.startDate.setMinutes(0);
          dateRange.startDate.setSeconds(0);
          convertedDate.startUnix = util.dateConverter(dateRange.startDate);
          // 検索の終了時間を設定(時刻情報は必要ないので削除する)
          dateRange.endDate.setHours(23);
          dateRange.endDate.setMinutes(59);
          dateRange.endDate.setSeconds(59);
          convertedDate.endUnix = util.dateConverter(dateRange.endDate);
        }
        return convertedDate;
      };
      const publishDateRange = convertDateRange(this.searchReleaseDateRange);

      // フィルタ実行
      const filteredFiles = this.fileDataListByLang
        .filter(pfc => { return filter.compareString(this.searchServiceNews, pfc.service_news_no, false); })
        .filter(pfc => {
          return filter.compareDate(publishDateRange.startUnix, publishDateRange.endUnix, util.dateConverter(pfc.release_date));
        });
      this.totalItemLength = filteredFiles.length;

      // 検索表示用にデータをバッファリング
      // テーブルデータに設定するlistにフィルタ
      const resultFileItems = this.pageSizeFilter(filteredFiles);
      this.targetUnitDetails = this.convertTableItem(resultFileItems);
    },
    convertTableItem: function (data) {
      const convertData = data;
      convertData.forEach((el) => {
        // UNIX時間⇒文字列
        if (el.published_time)
          el.published_time = util.timeConverter(el.published_time);

        if (el.is_two_step_pfc) {
          const columnNameList = Object.keys(el);
          el.fieldSpec = el.fieldSpec || {};
          columnNameList.forEach(item => el.fieldSpec[item] = config.LIST_ITEM_BOLD);
        }
      });
      return convertData;
    },
    // 検索条件をクリアする
    clear: function () {
      this.searchServiceNews = '';
      this.searchReleaseDateRange = { startDate: null, endDate: null };
    },

    // ページネーション関連
    initPagination() {
      this.currentPage = 1;
      if (this.$refs.paginationPFC) this.$refs.paginationPFC.initializePagenation();
    },
    // 設定されたページサイズでフィルタリングを実施する
    pageSizeFilter(buffer) {
      let result = [];
      result = buffer.filter((row, index) => {
        const start = (this.currentPage - 1) * this.pageSize;
        const end = this.currentPage * this.pageSize;
        let result = false;
        if (index >= start && index < end) result = true;
        return result;
      });
      return result;
    },
    // pagination コントロール選択イベント 
    selectedPage(currentPage) {
      this.currentPage = currentPage;
      this.executeFilter(false);
    },

    // データの初期化処理
    async initData() {
      await this.updateUnitInfo();
      this.totalItemLength = this.fileDataListByLang.length;
      this.initPagination();
      this.targetUnitDetails = this.convertTableItem(this.pageSizeFilter(structuredClone(this.fileDataListByLang)));
      this.clear();
    },

    // 複数ファイルのモーダル表示
    showMultipleFileAlert() {
      if (this.totalItemLength > 1 && util.getPreviousDisplayName(this.$route) !== 'systemInformation') {
        alert.showInfo(this, null, this.$t('message.message_multiple_pfc_file'));
      }
    },

    // 表示言語切り替え関連---------------------------------------------
    // 翻訳言語選択リストを生成
    updateTranslationList: function () {
      // リスト初期化
      this.translationLangs = [];
      // リストを生成
      const transList = [];
      this.allFileDataList.forEach(allList => allList.translations.forEach(list => transList.push(list)));
      const noDupDataList = Array.from(
        new Map(transList.map((list) => [list.language, list])).values()
      );
      for (const i in noDupDataList) {
        this.translationLangs.push({
          text: lngSwitch.getLabel(this.masterTranslations, noDupDataList[i].language, this.displayLang),
          value: noDupDataList[i].language
        });
      }
    },
    // 選択言語に基づくリストを作成
    createListBySelectedLang: function () {
      this.fileDataListByLang = [];
      // translationデータのみ抜き出し
      const translationList = [];
      this.allFileDataList.forEach(allList => allList.translations.forEach(transList => translationList.push(transList)));

      this.selectedLanguage = lngSwitch.extractSelectedLanguage(translationList, this.cachedTranslationLang, this.displayLang);

      this.allFileDataList.forEach((allList) => {
        const targetTranslation = allList.translations.find(trans => trans.language === this.selectedLanguage);
        if (targetTranslation === undefined) {
          // 対象の言語情報がない場合デフォルト言語を選択し、アップデートの内容を空で表示
          this.fileDataListByLang.push({
            language: null,
            pfc_file_id: allList.pfc_id,
            reproTypeMode: allList.reproTypeMode,
            service_news_no: null,
            service_news_contents: null,
            release_date: util.timeConverter(allList.released_time, null, 'day')
          });
        } else {
          this.fileDataListByLang.push({
            language: targetTranslation.language,
            pfc_file_id: allList.pfc_id,
            reproTypeMode: allList.reproTypeMode,
            service_news_no: targetTranslation.service_news ? targetTranslation.service_news.map(item => item).join(lngSwitch.getServiceNewsSeparator()) : null,
            service_news_contents: targetTranslation.details,
            release_date: util.timeConverter(allList.released_time, null, 'day')
          });
        }
      });
    },
    // サービスニュース・詳細・注意事項の表示言語が変更されたときの処理
    updateLanguage: function (selected) {
      if (selected) {
        this.setUserCache({
          key: config.USER_CACHE_REPRO_SYSTEM_TRANSLATION,
          value: selected
        });
        // 言語切り替えでキャッシュしていた情報を切り替える
        this.createListBySelectedLang();
        this.targetUnitDetails = this.convertTableItem(this.pageSizeFilter(structuredClone(this.fileDataListByLang)));
      }
    },
  },
  created() {
    if (this.displayReproTypeMode === 'campaign') {
      this.systemTableColumns = [{ name: 'service_news_no', label: 'message.header_vehicle_file_table_service_news', minWidth: '100px' },
      { name: 'service_news_contents', label: 'message.header_vehicle_file_table_countermeasure_content', minWidth: '400px' },
      { name: 'release_date', label: 'message.header_vehicle_file_table_release_date', minWidth: '100px' },
      ];
    } else {
      this.systemTableColumns = [{ name: 'service_news_no', label: 'message.header_vehicle_file_table_program_title', minWidth: '100px' },
      { name: 'service_news_contents', label: 'message.header_vehicle_file_table_program_content', minWidth: '400px' },
      { name: 'release_date', label: 'message.header_vehicle_file_table_release_date', minWidth: '100px' },
      ];
    }

    //System画面へ遷移した場合にタブ表示・非表示を親に通知
    this.$emit('setVehicleTabsAvailable', false);
  },

  async mounted() {
    await this.initData();
    if (this.isReprogrammable) {
      this.showMultipleFileAlert();
    }
  },
};